/**
 * Which part of the account a session is in, decided once.
 *
 * The account holds two consoles, not one console with some controls dimmed.
 * A provider's operator runs a fleet: a list of tenancies, a card for each, and
 * the version board. A customer's representative runs one tenancy: its
 * addresses, how its people sign in, and what it used. The two have almost
 * nothing in common — a representative has no use for a list of one row with a
 * seven-state filter over it, and no business reading a board of somebody
 * else's fleet — so they are given different addresses rather than the same
 * ones with the buttons taken out.
 *
 * Which side an account is on is read off the capability the server sends and
 * never off the name of the role, for the same reason every other gate here is:
 * a second copy of the rule in the console is a rule that will disagree.
 * `choose_identity_provider` is the one that says "this account represents one
 * tenancy" — the server withholds it from every provider role, precisely
 * because somebody who could point a customer's cluster at an identity provider
 * could point it at one whose tokens they mint.
 *
 * None of this is enforcement. The server refuses whatever the console lets
 * through, and every screen still handles a refusal.
 */

import type { AccountSession } from '../api/types';
import type { AccountRoute, RouteName } from '../router/routes';
import { can, mayChooseIdentityProvider, mayRollOutVersion } from './capabilities';

/** Whether this account acts for one tenancy rather than for the provider. */
export function isTenancyRepresentative(session: AccountSession): boolean {
  return mayChooseIdentityProvider(session);
}

/**
 * Every address this session may be at.
 *
 * The two trees do not overlap: a representative has no `/tenants`, no
 * `/tenants/{slug}` and no `/rollout`, and provider staff have no `/tenancy`.
 * `/password` is the one address both sides carry, because both sides have a
 * password.
 *
 * The version board moves under `roll_out_version` rather than `view_tenant`.
 * The support desk does nothing on it — it reads a tenancy's version off that
 * tenancy's own card — and a board of the whole fleet is the provider's working
 * surface.
 */
export function allowedRoutes(session: AccountSession): RouteName[] {
  const allowed: RouteName[] = ['account.password'];

  if (isTenancyRepresentative(session)) {
    allowed.push('account.tenancy', 'account.tenancyUsage', 'account.identityProvider');
    return allowed;
  }

  if (can(session, 'view_tenant')) {
    // Consumption reads under `view_tenant`, the same capability a card reads
    // under: an invoice a customer cannot check is one they have to take on
    // trust, and the desk answers questions about it.
    allowed.push('account.tenants', 'account.tenant', 'account.usage');
  }
  if (mayRollOutVersion(session)) allowed.push('account.rollout');

  return allowed;
}

/**
 * Where a session lands when it arrives somewhere it has no business.
 *
 * Always an address the same session may be at, which is why it is a chain and
 * not one answer: a home nobody is allowed at would send the console round the
 * same redirect for ever. The last link is the password, which every account
 * has.
 */
export function homeFor(session: AccountSession): AccountRoute {
  if (isTenancyRepresentative(session)) return { name: 'account.tenancy' };
  if (can(session, 'view_tenant')) return { name: 'account.tenants' };
  if (mayRollOutVersion(session)) return { name: 'account.rollout' };
  return { name: 'account.password' };
}
